// Custom ATC Button with Quantity Selector Functionality

class CustomATCManager {
  constructor() {
    this.debounceDelay = 140;
    this.updateTimeouts = new Map();
    this.cartData = null;
    this.lastCartFetchAt = 0;
    this.cartCacheTTL = 250;
    this.cartFetchPromise = null;
    this.pendingRefreshTimeout = null;
    this.lastRenderedCartCount = null;
    this.sectionRefreshScheduled = false;
    this.pendingDrawerOpen = false;
    this.isInitialized = false;
    this.maxAvailableQty = new Map(); // Store max available qty for each variant
    this.bulkQuantityMap = new Map(); // Store allowed stepped quantities for bulk products
    this.cartAnimStates = new Map(); // atcBtn -> { completeTimer }
    // Both of these are keyed by the qtySelector DOM element, not the
    // variant id — the same product/variant can appear in more than one
    // place on a page (main grid + a recommendations carousel, say),
    // and those are separate button instances that must not lock or
    // interfere with each other.
    this.pendingQtyRequests = new Set(); // qtySelector elements with a request in flight
    this.queuedQtyUpdates = new Map(); // qtySelector -> latest { variantId, newQty, form } queued while in flight

    this.init();
  }
    
  init() {
    if (this.isInitialized) return;

    // Bind every ATC button's click handler right away, synchronously.
    // This must NOT wait on the /cart.json fetch below: while that
    // network call is pending, the buttons are still plain
    // <button type="submit"> inside a real <form action="/cart/add">.
    // A click in that window falls through to the browser's native form
    // submit (no AJAX, no preventDefault) and Shopify redirects straight
    // to the /cart page — which is exactly the "sometimes goes to the
    // cart page" bug this fixes.
    this.initializeAllATCButtons();
    this.isInitialized = true;

    // Cart data (to show the right "already in cart" qty-selector state)
    // loads separately and just updates the UI once it's ready.
    this.getCartData().then(() => {
      this.updateAllATCStates();
    });

    // Listen for cart updates from other sources
    document.addEventListener('custom-cart-update', () => {
      this.scheduleCartRefresh();
    });
    
    if (window.PUB_SUB_EVENTS) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        // If it's from other sources (not our own updates), refresh UI
        if (event.source !== 'custom-atc' && event.source !== 'custom-atc-qty') {
          this.scheduleCartRefresh();
        }
      });
    }

    // Also listen to specific cart-items changes
    document.addEventListener('cart:updated', (event) => {
      if (event?.detail?.source === 'custom-atc') return;
      this.scheduleCartRefresh();
    });

    // Listen for cart builds (when items removed)
    document.addEventListener('cart:build', (event) => {
      if (event?.detail?.source === 'custom-atc') return;
      this.scheduleCartRefresh();
    });

    window.addEventListener('animeal:cart:changed', (event) => {
      const cartData = event?.detail?.cart;
      if (cartData && Array.isArray(cartData.items)) {
        this.syncUIFromCartData(cartData);
        return;
      }
      this.scheduleCartRefresh();
    });

    window.addEventListener('animeal:cart-drawer:rendered', (event) => {
      const cartData = event?.detail?.cart;
      if (cartData && Array.isArray(cartData.items)) {
        this.syncUIFromCartData(cartData);
        return;
      }
      this.scheduleCartRefresh();
    });
    this.bindPrescriptionResume(); 
  }

  getCartData(force = false) {
    const now = Date.now();
    if (!force && this.cartData && now - this.lastCartFetchAt < this.cartCacheTTL) {
      return Promise.resolve(this.cartData);
    }

    if (this.cartFetchPromise) {
      return this.cartFetchPromise;
    }

    this.cartFetchPromise = fetch('/cart.json', { credentials: 'same-origin', cache: 'no-store' })
      .then((response) => response.json())
      .then((data) => {
        this.cartData = data;
        this.lastCartFetchAt = Date.now();
        return data;
      })
      .catch((error) => {
        console.error('Error fetching cart data:', error);
      })
      .finally(() => {
        this.cartFetchPromise = null;
      });

    return this.cartFetchPromise;
  }

  scheduleCartRefresh(delay = 80, force = false) {
    if (this.pendingRefreshTimeout) clearTimeout(this.pendingRefreshTimeout);
    this.pendingRefreshTimeout = setTimeout(() => {
      this.pendingRefreshTimeout = null;
      this.refreshCartData(force);
    }, delay);
  }

  refreshCartData(force = false) {
    return this.getCartData(force).then((data) => {
      if (!data) return;
      this.syncUIFromCartData(data);
    });
  }

  syncUIFromCartData(cartData) {
    this.cartData = cartData;
    this.updateCartIconCount();
    this.updateAllATCStates();
    // Single announcement of "here is the cart as it now stands" that
    // anything else on the page can listen to — the floating "View cart"
    // bar (view-cart-bar.js) uses it instead of refetching /cart.js.
    window.dispatchEvent(new CustomEvent('animeal:cart:state', { detail: { cart: cartData } }));
  }

  initializeAllATCButtons() {
    // Find all product forms with ATC buttons. ".cvp-row-form" (the
    // card "N more options" popup rows) is excluded: several rows in
    // that popup can share one Shopify variant id (quantity packs),
    // which this manager can't distinguish since it tracks cart state
    // purely by variant id — every row would show the same "in cart"
    // state. custom-new.js owns a separate variant+line-item-property
    // aware add/sync implementation for those rows instead.
    const productForms = document.querySelectorAll('product-form form:not(.cvp-row-form)');

    productForms.forEach((form) => {
      this.initializeATCButton(form);
    });
  }

  initializeATCButton(form) {
    // The main product page's own Add to Cart button (buy-buttons.liquid,
    // rendered inside the "MainProduct-…" / "MainProductNew-…" section)
    // must stay completely untouched — no flying-cart animation, no
    // quantity-selector swap, just Shopify's normal add-to-cart behavior.
    // This whole widget is only for product CARDS (collection grids,
    // "you may also like", etc.) and the options popup (handled
    // separately in custom-new.js).
    if (form.closest('[id^="MainProduct"]')) return;

    const atcButton = form.querySelector('[type="submit"][name="add"], .custom-atc-btn');
    if (!atcButton) return;

    // Already fully wired up (wrapper, qty selector, click handler) —
    // bail out instead of re-running the DOM setup below. Without this,
    // a second call on an already-initialized button (e.g. because it
    // got moved elsewhere in the DOM, which the MutationObserver below
    // treats as a newly-added node) would wrap it a second time and
    // corrupt the widget (nested wrapper, orphaned quantity selector).
    if (atcButton.dataset.customAtcBound === 'true') return;

    const productForm = form.closest('product-form');
    if (!productForm) return;

    const variantIdInput = form.querySelector('input[name="id"]');
    if (!variantIdInput) return;

    const variantId = parseInt(variantIdInput.value);
    if (!Number.isFinite(variantId)) return;
    const wrapper = atcButton.closest('.quick-add') || atcButton.parentElement;

    // Get max available quantity from form data attribute
    const maxAvailable = parseInt(form.getAttribute('data-max-available')) || 999;
    this.maxAvailableQty.set(variantId, maxAvailable);
    this.bulkQuantityMap.set(variantId, this.getBulkQuantityOptions(form));

    // Create wrapper if needed
    let customWrapper = wrapper.querySelector('.custom-atc-wrapper');
    if (!customWrapper) {
      customWrapper = document.createElement('div');
      customWrapper.className = 'custom-atc-wrapper';
      atcButton.parentElement.insertBefore(customWrapper, atcButton);
      customWrapper.appendChild(atcButton);
    }

    // Remove old classes that might interfere
    atcButton.classList.remove('ATC_button', 'quick-add__submit', 'button', 'button--full-width', 'button--secondary');
    atcButton.classList.add('custom-atc-btn');

    this.ensureCartAnimationMarkup(atcButton);

    // Create quantity selector
    let qtySelector = wrapper.querySelector('.custom-qty-selector-wrapper');
    if (!qtySelector) {
      qtySelector = this.createQuantitySelector(variantId, form);
      customWrapper.appendChild(qtySelector);
    }

    // Check if product is in cart and set initial state
    this.updateFormATCState(form, variantId);

    // Attach event listeners
    if (atcButton.dataset.customAtcBound !== 'true') {
      atcButton.dataset.customAtcBound = 'true';
      atcButton.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleATCClick(e, form, customWrapper, variantId);
      });
    }
  }

  ensureCartAnimationMarkup(atcButton) {
    // Default label: the existing "Add to Cart" / "Add" text span.
    const defaultLabel = atcButton.querySelector('span:not(.sold-out-message)');
    if (defaultLabel && !defaultLabel.classList.contains('atc-label-default')) {
      defaultLabel.classList.add('atc-label-default');
    }

    // Plain white spinner shown while the add-to-cart request is in flight.
    if (!atcButton.querySelector('.atc-spinner')) {
      const spinner = document.createElement('span');
      spinner.className = 'atc-spinner';
      spinner.setAttribute('aria-hidden', 'true');
      atcButton.appendChild(spinner);
    }
  }

  // Shows the white spinner on the button for as long as the add-to-cart
  // request is in flight. Call resolveCartAnimation() when the request
  // succeeds — it hides the spinner and fires 'atc-animation-complete'
  // on the button, which is what hands over to the quantity selector.
  startCartAnimation(atcBtn) {
    if (!atcBtn) return;
    this.resetCartAnimation(atcBtn);
    this.ensureCartAnimationMarkup(atcBtn);
    atcBtn.classList.add('atc-spinning');
    this.cartAnimStates.set(atcBtn, {});
  }

  resolveCartAnimation(atcBtn) {
    if (!atcBtn) return;
    const state = this.cartAnimStates.get(atcBtn) || {};
    atcBtn.classList.remove('atc-spinning');
    // Async on purpose: callers attach their 'atc-animation-complete'
    // listener after calling this, so a synchronous dispatch would miss it.
    state.completeTimer = setTimeout(() => {
      this.cartAnimStates.delete(atcBtn);
      atcBtn.dispatchEvent(new CustomEvent('atc-animation-complete'));
    }, 0);
    this.cartAnimStates.set(atcBtn, state);
  }

  // Cancels/cleans up any in-flight spinner state on this button (e.g. the
  // add-to-cart request failed, or the button is being reset for reuse).
  resetCartAnimation(atcBtn) {
    if (!atcBtn) return;
    const state = this.cartAnimStates.get(atcBtn);
    if (state && state.completeTimer) clearTimeout(state.completeTimer);
    this.cartAnimStates.delete(atcBtn);
    atcBtn.classList.remove('atc-spinning');
  }

  createQuantitySelector(variantId, form) {
    const wrapper = document.createElement('div');
    wrapper.className = 'custom-qty-selector-wrapper';
    wrapper.dataset.variantId = String(variantId);

    const minusBtn = document.createElement('button');
    minusBtn.className = 'custom-qty-btn custom-qty-btn-minus';
    minusBtn.type = 'button';
    minusBtn.innerHTML = '-';
    minusBtn.setAttribute('aria-label', 'Decrease quantity');

    const display = document.createElement('div');
    display.className = 'custom-qty-display';
    display.innerHTML = '<span class="qty-value">0</span>';

    const plusBtn = document.createElement('button');
    plusBtn.className = 'custom-qty-btn custom-qty-btn-plus';
    plusBtn.type = 'button';
    plusBtn.innerHTML = '+';
    plusBtn.setAttribute('aria-label', 'Increase quantity');

    // Add max quantity message element
    const maxQtyMsg = document.createElement('div');
    maxQtyMsg.className = 'custom-qty-max-msg hidden';
    maxQtyMsg.innerHTML = '<small>Max available: <span class="max-qty-text">0</span></small>';

    wrapper.appendChild(minusBtn);
    wrapper.appendChild(display);
    wrapper.appendChild(plusBtn);
    wrapper.appendChild(maxQtyMsg);

    minusBtn.addEventListener('click', () => this.handleQuantityChange(-1, form, wrapper, variantId));
    plusBtn.addEventListener('click', () => this.handleQuantityChange(1, form, wrapper, variantId));

    return wrapper;
  }

 handleATCClick(event, form, wrapper, variantId) {
  event.preventDefault();
  const atcBtn = wrapper.querySelector('.custom-atc-btn');

  // Only this specific button is blocked while its own request is in
  // flight — other products' buttons must stay fully clickable/hoverable.
  if (atcBtn && atcBtn.getAttribute('aria-disabled') === 'true') return;

  // Prescription products -> open popup instead of adding
  if (this.isPrescriptionRequired(form)) {
    this.openPrescriptionGate(form, wrapper, variantId);
    return;
  }

  // Normal products -> unchanged behaviour
  this.performAdd(form, wrapper, variantId, null);
}

isPrescriptionRequired(form) {
  // Card-level: the medicine card renders its own hidden prescription property input.
  if (form && form.querySelector('input.custom-text[name="properties[Prescription]"]')) {
    return true;
  }

  // Product-page main product: detected via the global prescription badge button,
  // but ONLY for the main form — never for card/quick-add forms on the same page.
  const isCardForm = form && form.closest('.card-wrapper, .quick-add');
  if (!isCardForm &&
      (window.NPD_PRESCRIPTION_REQUIRED ||
       document.getElementById('prescription_popup_btn') ||
       document.getElementById('prescription_popup_btn_mobile'))) {
    return true;
  }

  return false;
}

openPrescriptionGate(form, wrapper, variantId) {
  const popup = document.getElementById('upload-prescription');
  if (!popup) {
    // No popup on page -> don't block the sale, just add
    this.performAdd(form, wrapper, variantId, null);
    return;
  }

  const qty = parseInt(form.querySelector('input[name="quantity"]')?.value || '1', 10) || 1;
  this._pendingPrescriptionAdd = { form, wrapper, variantId, quantity: qty };

  // reset the popup so a stale file/preview doesn't carry over
  const fileInput = popup.querySelector('#presc-img-upload');
  if (fileInput) fileInput.value = '';
  const out = popup.querySelector('#presc-img-output');
  if (out) out.innerHTML = '';

  popup.style.display = 'flex';
}
openPrescriptionGateFor(action, payload) {
  const popup = document.getElementById('upload-prescription');
  if (!popup) {
    if (action === 'buynow') this.runBuyNow(payload, null);
    return;
  }

  this._pendingPrescriptionAdd = { action, ...payload };

  const fileInput = popup.querySelector('#presc-img-upload');
  if (fileInput) fileInput.value = '';
  const out = popup.querySelector('#presc-img-output');
  if (out) out.innerHTML = '';

  popup.style.display = 'flex';
}

runBuyNow(payload, extra) {
  const variantId = payload.variantId;
  const quantity = parseInt(payload.quantity || 1, 10) || 1;
  const btn = payload.btn;
  const oldHTML = btn ? btn.innerHTML : '';

  if (btn) {
    btn.innerHTML = 'Redirecting...';
    btn.disabled = true;
    btn.style.opacity = '0.7';
    btn.style.pointerEvents = 'none';
  }

  const restoreBtn = () => {
    if (!btn) return;
    btn.innerHTML = oldHTML;
    btn.disabled = false;
    btn.style.opacity = '1';
    btn.style.pointerEvents = 'auto';
  };

  fetch(routes.cart_clear_url, { method: 'POST', credentials: 'same-origin' })
    .then(() => {
      const formData = new FormData();
      formData.append('id', String(parseInt(variantId, 10)));
      formData.append('quantity', String(quantity));

      const savedDoctorId = localStorage.getItem('doctor_id');
      if (savedDoctorId) formData.append('properties[doctorId]', savedDoctorId);

      if (extra) {
        if (extra.file) {
          formData.append('properties[Prescription]', extra.file, extra.file.name);
        } else if (extra.mode) {
          formData.append('properties[Prescription]', extra.mode);
        }
        if (extra.docName) formData.append('properties[Doctor]', extra.docName);
      }

      return fetch(routes.cart_add_url, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
        body: formData,
      });
    })
    .then((response) => {
      if (!response.ok) throw new Error('Buy Now add to cart failed');
      window.location.href = routes.cart_url + '/checkout';
    })
    .catch((err) => {
      console.error('Buy Now error:', err);
      restoreBtn();
      alert('Something went wrong. Please try again.');
    });
}
// Product-page main form: attach prescription props as hidden inputs, then
// resubmit so product-form.js does the actual add.
resumePageForm(form, extra) {
  const added = [];
  const addInput = (name, value) => {
    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    form.appendChild(input);
    added.push(input);
  };

  form.querySelectorAll('input[name="properties[Prescription]"], input[name="properties[Doctor]"]').forEach((el) => el.remove());

  if (extra && extra.file) {
    const fileInput = document.createElement('input');
    fileInput.type = 'file';
    fileInput.name = 'properties[Prescription]';
    fileInput.style.display = 'none';
    const dt = new DataTransfer();
    dt.items.add(extra.file);
    fileInput.files = dt.files;
    form.appendChild(fileInput);
    added.push(fileInput);
  } else if (extra && extra.mode) {
    addInput('properties[Prescription]', extra.mode);
  }
  if (extra && extra.docName) addInput('properties[Doctor]', extra.docName);

  form.dataset.rxDone = '1';
  if (typeof form.requestSubmit === 'function') form.requestSubmit();
  else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));

  // product-form.js reads FormData synchronously in the submit handler
  setTimeout(() => {
    delete form.dataset.rxDone;
    added.forEach((el) => el.remove());
  }, 0);
}

bindPrescriptionResume() {
  // Capture phase so we can intercept BEFORE the old global popup handlers run
  document.addEventListener('click', (e) => {
    const pending = this._pendingPrescriptionAdd;
    if (!pending) return;

    const popup = document.getElementById('upload-prescription');
    if (!popup || !popup.contains(e.target)) return;

    const uploadBtn = e.target.closest('#up-presc--upload_btn');
    const callBtn   = e.target.closest('#up-presc--call_later');
    const waBtn     = e.target.closest('#up-presc--whatsapp');

    // "Choose File" (#upload_choose_file) is NOT intercepted — let it open the picker
    if (!uploadBtn && !callBtn && !waBtn) return;

    e.preventDefault();
    e.stopImmediatePropagation(); // stop the old .custom-text based handlers

    let extra = null;
    if (uploadBtn) {
      const file = popup.querySelector('#presc-img-upload')?.files?.[0] || null;
      const docName = popup.querySelector('#doc-name')?.value || '';
      extra = { file, docName };
      if (!file) extra.mode = 'Uploaded'; // safety if no file picked
    } else if (callBtn) {
      extra = { mode: 'on Call' };
    } else if (waBtn) {
      extra = { mode: 'on WhatsApp' };
    }

  popup.style.display = 'none';

    this._pendingPrescriptionAdd = null;

    if (pending.action === 'buynow') {
      this.runBuyNow(pending, extra);
    } else if (pending.action === 'cvprow') {
      if (typeof window.cvpAddRowToCart === 'function') window.cvpAddRowToCart(pending.form, extra || { mode: 'Uploaded' });
    } else if (pending.action === 'pageform') {
      this.resumePageForm(pending.form, extra);
    } else {
      this.performAdd(pending.form, pending.wrapper, pending.variantId, extra);
    }

    if (waBtn) {
      // open WhatsApp after the add if you want (optional)
      // window.open('https://wa.me/...', '_blank');
    }
  }, true);
}

performAdd(form, wrapper, variantId, extra) {
  const atcBtn = wrapper.querySelector('.custom-atc-btn');

  // Lock only THIS button — other products' buttons must stay fully
  // normal (clickable, hoverable, no color/opacity change) while this
  // one is adding.
  atcBtn.disabled = true;
  atcBtn.setAttribute('aria-disabled', 'true');
  window.dispatchEvent(new CustomEvent('animeal:cart:request-start', {
    detail: {
      source: 'custom-atc',
      variantId: Number(variantId),
      quantity: Number(form.querySelector('input[name="quantity"]')?.value || 1),
    },
  }));

  const btnText = atcBtn.querySelector('.atc-label-default') || atcBtn.querySelector('span') || atcBtn;
  btnText.dataset.originalText = btnText.textContent;

  this.startCartAnimation(atcBtn);
  atcBtn.classList.add('loading');

  const config = fetchConfig('javascript');
  config.headers['X-Requested-With'] = 'XMLHttpRequest';
  delete config.headers['Content-Type'];

  const formData = new FormData(form);
  const quantity = parseInt(formData.get('quantity')) || 1;
  const maxAvailable = this.maxAvailableQty.get(variantId) || 999;

  if (quantity > maxAvailable) {
    atcBtn.disabled = false;
    atcBtn.setAttribute('aria-disabled', 'false');
    window.dispatchEvent(new CustomEvent('animeal:cart:request-end', {
      detail: { source: 'custom-atc', variantId: Number(variantId) },
    }));
    this.showMaxAvailableWarning(wrapper, maxAvailable, variantId);
    btnText.textContent = btnText.dataset.originalText || 'Add to Cart';
    this.showLoader(atcBtn, false);
    atcBtn.classList.remove('loading');
    this.resetCartAnimation(atcBtn);
    return;
  }

  const savedDoctorId = localStorage.getItem('doctor_id');
  if (savedDoctorId) formData.append('properties[doctorId]', savedDoctorId);

// Prescription info from the popup
  if (extra) {
    // Card renders empty, required placeholder props — remove them so we don't
    // send a duplicate/empty Prescription key alongside the real value.
    formData.delete('properties[Prescription]');
    formData.delete("properties[Doctor's Name]");

    if (extra.file) {
      formData.append('properties[Prescription]', extra.file, extra.file.name);
    } else if (extra.mode) {
      formData.append('properties[Prescription]', extra.mode);
    }
    if (extra.docName) formData.append('properties[Doctor]', extra.docName);
  }
  this.appendSectionsToFormData(formData);
  config.body = formData;

  fetch(`${routes.cart_add_url}`, config)
    .then((response) => response.json())
    .then((response) => {
      // Shopify can add fewer than requested (stock ran out mid-request)
      // and still respond with status 422 + "Only N items were added to
      // your cart due to availability." — that's a partial success, not
      // a failure: N items really are in the cart, so it must not take
      // the early-return failure path below (which used to skip the
      // cart refresh entirely).
      const partialMessage = response.description || response.message || '';
      const partiallyAdded = response.status === 422 && /were added/i.test(partialMessage);

      if (response.status && !partiallyAdded) {
        console.error('Error adding to cart:', response);
        this.showLoader(atcBtn, false);
        atcBtn.classList.remove('loading');
        this.resetCartAnimation(atcBtn);
        btnText.textContent = btnText.dataset.originalText || 'Add to Cart';
        atcBtn.disabled = false;
        atcBtn.setAttribute('aria-disabled', 'false');
        window.dispatchEvent(new CustomEvent('animeal:cart:request-end', {
          detail: { source: 'custom-atc', variantId: Number(variantId) },
        }));
        return;
      }

      // Let the cart animation play out (slide to the right) — the
      // quantity selector only takes over once it actually finishes,
      // via the 'atc-animation-complete' listener below.
      this.resolveCartAnimation(atcBtn);

      this.showCartSuccessBadge(
        partiallyAdded
          ? `⚠️ <strong>${partialMessage}</strong>`
          : `
        🛒 <strong>Item Added</strong><br>
        Click the cart icon to checkout.
      `
      );

      // The clamped quantity Shopify actually added isn't in this
      // response (only the message text is) — skip the optimistic bump
      // here and let the forced refetch below (scheduleCartRefresh with
      // no response.sections) sync the real count instead of guessing.
      if (!partiallyAdded) this.applyOptimisticAddToCart(variantId, quantity);

      if (this.cartData) {
        this.syncUIFromCartData(this.cartData);
      } else {
        this.showQuantitySelector(wrapper, quantity, variantId, form);
      }
      this.showLoader(atcBtn, false);

      if (response.sections) {
        this.updateCartSectionsFromData(response.sections, false);
      } else {
        this.refreshCartDrawer(false);
        this.scheduleCartRefresh(180, true);
      }

      if (window.PUB_SUB_EVENTS) {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'custom-atc', cartData: response, variantId: variantId,
        });
      }

      window.dispatchEvent(new CustomEvent('custom-atc-updated', {
        detail: { variantId: variantId, quantity: quantity },
      }));

      const audioElement = form.closest('product-form')?.querySelector('.custom__animal_sound');
      if (audioElement?.classList?.contains('itsAnCatPrd')) {
        document.querySelector('.custom__animal_sound_thm audio')?.play().catch(() => {});
      }

      // Once the cart icon finishes sliding out (whether that happens
      // right away or only after a slow "hold" wait), reveal the
      // quantity selector. Nothing here is a fixed guess at timing —
      // it's tied to the actual animation finishing.
      atcBtn.addEventListener('atc-animation-complete', () => {
        const t = atcBtn.querySelector('.atc-label-default') || atcBtn.querySelector('span') || atcBtn;
        t.textContent = t.dataset.originalText || 'Add to Cart';
        atcBtn.classList.remove('loading');
        this.resetCartAnimation(atcBtn);
        atcBtn.disabled = false;
        atcBtn.setAttribute('aria-disabled', 'false');
        this.showLoader(atcBtn, false);
        this.updateFormATCState(form, variantId);
      }, { once: true });

      window.dispatchEvent(new CustomEvent('animeal:cart:request-end', {
        detail: { source: 'custom-atc', variantId: Number(variantId) },
      }));
    })
    .catch((error) => {
      console.error('Error adding to cart:', error);
      this.showLoader(atcBtn, false);
      atcBtn.classList.remove('loading');
      this.resetCartAnimation(atcBtn);
      btnText.textContent = btnText.dataset.originalText || 'Add to Cart';
      atcBtn.disabled = false;
      atcBtn.setAttribute('aria-disabled', 'false');
      window.dispatchEvent(new CustomEvent('animeal:cart:request-end', {
        detail: { source: 'custom-atc', variantId: Number(variantId) },
      }));
    });
}

  handleQuantityChange(delta, form, wrapper, variantId) {
    const display = wrapper.querySelector('.custom-qty-display .qty-value');
    const maxQtyMsg = wrapper.querySelector('.custom-qty-max-msg');
    const maxQtyText = wrapper.querySelector('.max-qty-text');
    const plusBtn = wrapper.querySelector('.custom-qty-btn-plus');

    const currentQty = parseInt(display.textContent) || 0;
    let newQty = currentQty;

    const maxAvailable = this.maxAvailableQty.get(variantId) || 999;
    const bulkOptions = this.getBulkOptionsForVariant(variantId, form, maxAvailable);

    if (bulkOptions.length) {
      if (delta > 0) {
        const nextBulkQty = bulkOptions.find((qty) => qty > currentQty);
        if (!nextBulkQty) {
          if (plusBtn) plusBtn.disabled = true;
          if (maxQtyMsg && maxQtyText) {
            maxQtyText.textContent = bulkOptions[bulkOptions.length - 1];
            maxQtyMsg.classList.remove('hidden');
            setTimeout(() => {
              maxQtyMsg.classList.add('hidden');
            }, 3000);
          }
          return;
        }
        newQty = nextBulkQty;
      } else if (delta < 0) {
        const lowerOptions = bulkOptions.filter((qty) => qty < currentQty);
        newQty = lowerOptions.length ? lowerOptions[lowerOptions.length - 1] : 0;
      }
    } else {
      newQty = Math.max(0, currentQty + delta);
    }

    // console.log(`[ATC] Quantity changed: ${currentQty} -> ${newQty} for variant ${variantId} (Max: ${maxAvailable})`);

    if (newQty > maxAvailable) {
      // console.log(`[ATC] Quantity ${newQty} exceeds max available ${maxAvailable}`);
      if (maxQtyMsg && maxQtyText) {
        maxQtyText.textContent = maxAvailable;
        maxQtyMsg.classList.remove('hidden');
        setTimeout(() => {
          maxQtyMsg.classList.add('hidden');
        }, 3000);
      }

      newQty = maxAvailable;
      display.textContent = newQty;
      if (plusBtn) plusBtn.disabled = true;
      return;
    }

    if (plusBtn && newQty < maxAvailable) {
      plusBtn.disabled = false;
    }

    if (maxQtyMsg) {
      maxQtyMsg.classList.add('hidden');
    }

    display.textContent = newQty;

    // Switch back to "Add to Cart" the moment quantity hits 0, instead of
    // waiting on the network round-trip to do it — that round-trip can
    // be slow, race against another in-flight request for this same
    // variant, or occasionally never resolve the UI (this is what was
    // leaving "0" stuck with the +/- selector still showing). The actual
    // removal is still sent to Shopify via debouncedCartUpdate below; if
    // that request ever comes back saying the item is still in the cart
    // (e.g. it failed), the next cart sync will restore the quantity
    // selector with the real quantity automatically.
    if (newQty === 0) {
      const outerWrapper = wrapper.closest('.custom-atc-wrapper') || wrapper.parentElement;
      if (outerWrapper) {
        this.showATCButton(outerWrapper);
        // The button is back to "Add to Cart", but the actual removal
        // is still happening in the background — show it as busy/
        // loading (rather than looking fully idle) until that request
        // settles, via updateCartQuantity's finishThisVariant().
        const atcBtn = outerWrapper.querySelector('.custom-atc-btn');
        if (atcBtn) {
          atcBtn.disabled = true;
          atcBtn.setAttribute('aria-disabled', 'true');
          atcBtn.classList.add('loading');
          this.showLoader(atcBtn, true);
        }
      }
    }

    this.debouncedCartUpdate(variantId, newQty, form, wrapper);
  }

  debouncedCartUpdate(variantId, newQty, form, wrapper) {
    // Keyed by the qtySelector element (wrapper), not the variant id —
    // the same product/variant can have more than one qty-selector
    // instance on the page (main grid + a recommendations carousel,
    // say). Keying by variant id let one instance's debounce silently
    // cancel/overwrite another instance's pending update.
    if (this.updateTimeouts.has(wrapper)) {
      clearTimeout(this.updateTimeouts.get(wrapper));
    }

    const timeout = setTimeout(() => {
      this.updateCartQuantity(variantId, newQty, form, wrapper);
      this.updateTimeouts.delete(wrapper);
    }, this.debounceDelay);

    this.updateTimeouts.set(wrapper, timeout);
  }

  updateCartQuantity(variantId, newQty, form, qtySelector) {
    // Only guard THIS specific qty-selector instance against a second
    // overlapping request — a different product's +/-, or even another
    // on-page instance of this SAME product/variant, must keep working.
    // If the user clicked again while the previous request for this
    // instance was still in flight, don't just drop it: remember the
    // latest desired quantity and fire it the moment the current request
    // finishes (see finishThisVariant below). Previously this silently
    // did nothing, which looked like the button "sometimes not working".
    if (this.pendingQtyRequests.has(qtySelector)) {
      this.queuedQtyUpdates.set(qtySelector, { variantId, newQty, form });
      return;
    }

    const cartItem = this.getCartItemByVariantId(variantId);

    // Identify the cart line by its unique `key`, not by its positional
    // index. The index is only as fresh as our last cached cart.json —
    // if it had drifted even slightly from the server's real line order
    // (another product added/removed elsewhere), updating "line N" could
    // silently change a *different* product's line. The key is stable
    // and unambiguous regardless of ordering.
    if (!cartItem || !cartItem.key) {
      // Our local cart cache doesn't have this line (or is missing its
      // key) right now — this used to just silently bail out here,
      // which is exactly what caused the UI to show "Add to Cart" while
      // the item was still actually sitting in the cart on Shopify's
      // side. Instead, force a fresh /cart.json fetch and retry against
      // that: if the line really is there, we now have its key and the
      // removal/update actually goes out.
      this.getCartData(true).then((freshData) => {
        this.cartData = freshData;
        const freshItem = this.getCartItemByVariantId(variantId);
        if (freshItem && freshItem.key) {
          this.updateCartQuantity(variantId, newQty, form, qtySelector);
        } else if (newQty === 0) {
          // Still not found — it's genuinely already gone. Nothing more
          // to send, so stop showing the ATC button as busy.
          this.clearZeroQtyLoadingState(qtySelector);
        }
      });
      return;
    }

    const lineKey = cartItem.key;

    this.pendingQtyRequests.add(qtySelector);
    qtySelector.classList.add('loading');

    // Disable only this product's own +/- buttons — other products'
    // buttons and cards must stay fully normal.
    const minusBtn = qtySelector.querySelector('.custom-qty-btn-minus');
    const plusBtn = qtySelector.querySelector('.custom-qty-btn-plus');
    if (minusBtn) minusBtn.disabled = true;
    if (plusBtn) plusBtn.disabled = true;

    window.dispatchEvent(
      new CustomEvent('animeal:cart:request-start', {
        detail: {
          source: 'custom-atc-qty',
          variantId: Number(variantId),
          quantity: Number(newQty),
        },
      })
    );

    this.showLoader(qtySelector, true);

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    const body = JSON.stringify({
      id: lineKey,
      quantity: newQty,
      sections: this.getSectionsToRender()
    });
    config.body = body;

    const finishThisVariant = () => {
      this.pendingQtyRequests.delete(qtySelector);
      this.updateQuantityButtonStates(qtySelector, parseInt(qtySelector.querySelector('.qty-value')?.textContent, 10) || 0, variantId);

      // A click landed while this request was in flight — apply it now.
      const queued = this.queuedQtyUpdates.get(qtySelector);
      if (queued) {
        this.queuedQtyUpdates.delete(qtySelector);
        this.updateCartQuantity(queued.variantId, queued.newQty, queued.form, qtySelector);
        return;
      }

      // This was a "reduce to 0" request settling with nothing queued
      // after it — the ATC button was shown as busy the moment the user
      // clicked; now that the removal has actually finished, stop
      // showing it as busy.
      if (newQty === 0) this.clearZeroQtyLoadingState(qtySelector);
    };

    fetch(`${routes.cart_change_url}`, config)
      .then((response) => response.json())
      .then((response) => {
        if (response.errors) {
          console.error('Error updating cart:', response.errors);
          this.showLoader(qtySelector, false);
          qtySelector.classList.remove('loading');
          finishThisVariant();
          this.refreshCartData(true);
          window.dispatchEvent(
            new CustomEvent('animeal:cart:request-end', {
              detail: {
                source: 'custom-atc-qty',
                variantId: Number(variantId),
              },
            })
          );
          return;
        }

        // Update cart data/UI immediately from response.
        this.syncUIFromCartData(response);

        // Hide loader first
        this.showLoader(qtySelector, false);
        qtySelector.classList.remove('loading');

        const shouldOpenDrawer = false;

        if (response.sections) {
          this.updateCartSectionsFromData(response.sections, shouldOpenDrawer);
        } else {
          this.refreshCartDrawer(shouldOpenDrawer);
        }

        finishThisVariant();
        window.dispatchEvent(
          new CustomEvent('animeal:cart:request-end', {
            detail: {
              source: 'custom-atc-qty',
              variantId: Number(variantId),
            },
          })
        );

        // Trigger cart update events with source tracking
        if (window.PUB_SUB_EVENTS) {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'custom-atc-qty',
            cartData: response,
            variantId: variantId,
            quantity: newQty
          });
        }

        // Dispatch custom event for other listeners
        window.dispatchEvent(new CustomEvent('custom-atc-qty-updated', {
          detail: { variantId: variantId, quantity: newQty }
        }));
      })
      .catch((error) => {
        console.error('Error updating cart quantity:', error);
        this.showLoader(qtySelector, false);
        qtySelector.classList.remove('loading');
        finishThisVariant();
        this.refreshCartData(true);
        window.dispatchEvent(
          new CustomEvent('animeal:cart:request-end', {
            detail: {
              source: 'custom-atc-qty',
              variantId: Number(variantId),
            },
          })
        );
      });
  }

  updateCartIconCount() {
    if (!this.cartData || !this.cartData.items) {
      return;
    }

    const totalCount = this.cartData.items.reduce((sum, item) => sum + item.quantity, 0);
    if (this.lastRenderedCartCount === totalCount) return;
    
    const cartIcon = document.getElementById('cart-icon-bubble');
    
    if (cartIcon) {
      let countBubble = cartIcon.querySelector('.cart-count-bubble');
      if (!countBubble) {
        countBubble = document.createElement('div');
        countBubble.className = 'cart-count-bubble';
        cartIcon.appendChild(countBubble);
      }
      
      countBubble.innerHTML = `
        <span aria-hidden="true">${totalCount}</span><span class="visually-hidden">${totalCount} items</span>
      `;
      this.lastRenderedCartCount = totalCount;
    }
  }

  updateAllATCStates() {
    // Excludes ".cvp-row-form" for the same reason as initializeAllATCButtons().
    const productForms = document.querySelectorAll('product-form form:not(.cvp-row-form)');

    productForms.forEach((form) => {
      const variantIdInput = form.querySelector('input[name="id"]');
      if (!variantIdInput) return;

      const variantId = parseInt(variantIdInput.value);
      if (!Number.isFinite(variantId)) return;
      this.updateFormATCState(form, variantId);
    });
  }

  updateFormATCState(form, variantId) {
    const wrapper = form.parentElement.querySelector('.custom-atc-wrapper');
    if (!wrapper) return;

    const qtySelector = wrapper.querySelector('.custom-qty-selector-wrapper');
    const qtyValueElement = qtySelector?.querySelector('.custom-qty-display .qty-value');
    const cartItem = this.getCartItemByVariantId(variantId);
    const targetQty = cartItem?.quantity || 0;
    const isActive = qtySelector?.classList.contains('active');
    const currentQty = qtyValueElement ? parseInt(qtyValueElement.textContent, 10) || 0 : 0;

    if (targetQty > 0) {
      if (!isActive || currentQty !== targetQty) {
        this.showQuantitySelector(wrapper, targetQty, variantId, form);
      } else {
        this.updateQuantityButtonStates(qtySelector, targetQty, variantId);
      }
    } else if (isActive) {
      this.showATCButton(wrapper);
    }
  }

  showATCButton(wrapper, form = null, variantId = null) {
    const atcBtn = wrapper.querySelector('.custom-atc-btn');
    const qtySelector = wrapper.querySelector('.custom-qty-selector-wrapper');

    if (atcBtn) {
      atcBtn.classList.remove('hidden');
      atcBtn.removeAttribute('aria-disabled');
      atcBtn.classList.remove('loading');
      // Ensure button is enabled
      atcBtn.disabled = false;
      // Make sure it comes back showing "Add to Cart", not stuck mid-animation
      this.resetCartAnimation(atcBtn);

      // Hide any loader in button
      const btnLoader = atcBtn.querySelector('.loading__spinner');
      if (btnLoader) {
        btnLoader.classList.add('hidden');
      }

    }
    
    if (qtySelector) {
      qtySelector.classList.remove('active');
      // Hide loader in selector
      const selectorLoader = qtySelector.querySelector('.loading__spinner');
      if (selectorLoader) {
        selectorLoader.classList.add('hidden');
      }
      // Reset display value
      const display = qtySelector.querySelector('.custom-qty-display .qty-value');
      if (display) {
        display.textContent = '0';
      }
      // Reset buttons state
      const minusBtn = qtySelector.querySelector('.custom-qty-btn-minus');
      const plusBtn = qtySelector.querySelector('.custom-qty-btn-plus');
      if (minusBtn) minusBtn.disabled = false;
      if (plusBtn) plusBtn.disabled = false;
    }
  }

  // The ATC button was optimistically shown (and marked busy/loading) the
  // moment quantity hit 0, before the removal request even went out — see
  // handleQuantityChange(). Call this once that request has actually
  // settled (success, error, or turned out to be a no-op) to stop
  // showing it as busy and leave it as a normal, clickable "Add to Cart".
  clearZeroQtyLoadingState(qtySelector) {
    const outerWrapper = qtySelector.closest('.custom-atc-wrapper') || qtySelector.parentElement;
    if (outerWrapper) this.showATCButton(outerWrapper);
  }

showQuantitySelector(wrapper, quantity, variantId, form) {
  const atcBtn = wrapper.querySelector('.custom-atc-btn');

  // Don't swap the ATC button out for the quantity selector while its
  // add-to-cart request is still spinning — let it finish first
  // (the 'atc-animation-complete' callback in performAdd() re-triggers
  // this once it's done).
  // This is scoped to THIS button instance, not the variant id — the
  // same product can appear in more than one place on the page (main
  // grid + a recommendations carousel, say), and those are separate
  // buttons that must not block each other.
  if (this.cartAnimStates.has(atcBtn)) return;

  const qtySelector = wrapper.querySelector('.custom-qty-selector-wrapper');
  const display = qtySelector?.querySelector('.custom-qty-display .qty-value');

  if (display) display.textContent = quantity;

  if (qtySelector) {
    qtySelector.classList.add('active');
  }

  if (atcBtn) {
    atcBtn.classList.add('hidden');
    atcBtn.disabled = false;
    atcBtn.setAttribute('aria-disabled', 'false');
    atcBtn.classList.remove('loading');
  }

  if (qtySelector) {
    this.updateQuantityButtonStates(qtySelector, quantity, variantId);
  }
}

  updateQuantityButtonStates(qtySelector, quantity, variantId) {
    const minusBtn = qtySelector.querySelector('.custom-qty-btn-minus');
    const plusBtn = qtySelector.querySelector('.custom-qty-btn-plus');
    const maxQtyMsg = qtySelector.querySelector('.custom-qty-max-msg');
    const maxQtyText = qtySelector.querySelector('.max-qty-text');

    const maxAvailable = this.maxAvailableQty.get(variantId) || 999;
    const bulkOptions = this.getBulkOptionsForVariant(variantId, null, maxAvailable);

    if (bulkOptions.length) {
      const hasHigherBulkQty = bulkOptions.some((qty) => qty > quantity);

      if (minusBtn) {
        minusBtn.disabled = quantity <= 0;
      }

      if (plusBtn) {
        plusBtn.disabled = !hasHigherBulkQty;
        if (maxQtyMsg && maxQtyText) {
          if (!hasHigherBulkQty && quantity > 0) {
            maxQtyText.textContent = bulkOptions[bulkOptions.length - 1];
            maxQtyMsg.classList.remove('hidden');
          } else {
            maxQtyMsg.classList.add('hidden');
          }
        }
      }
      return;
    }

    if (minusBtn) {
      minusBtn.disabled = quantity <= 0;
    }

    if (plusBtn) {
      if (quantity >= maxAvailable) {
        plusBtn.disabled = true;
        if (maxQtyMsg && maxQtyText) {
          maxQtyText.textContent = maxAvailable;
          maxQtyMsg.classList.remove('hidden');
        }
      } else {
        plusBtn.disabled = false;
        if (maxQtyMsg) {
          maxQtyMsg.classList.add('hidden');
        }
      }
    }
  }

  getBulkQuantityOptions(form) {
    if (!form) return [];

    const inputs = Array.from(form.querySelectorAll('.custom__quantity_sel_cards'));
    if (!inputs.length) return [];

    const values = inputs
      .map((input) => parseInt(input.value, 10))
      .filter((qty) => Number.isFinite(qty) && qty > 0);

    return [...new Set(values)].sort((a, b) => a - b);
  }

  getBulkOptionsForVariant(variantId, form = null, maxAvailable = null) {
    let options = this.bulkQuantityMap.get(variantId) || [];
    if ((!options || !options.length) && form) {
      options = this.getBulkQuantityOptions(form);
      this.bulkQuantityMap.set(variantId, options);
    }

    if (!options.length) return [];
    if (maxAvailable === null || maxAvailable === undefined) return options;

    const capped = options.filter((qty) => qty <= maxAvailable);
    return capped.length ? capped : [];
  }

  getCartItemByVariantId(variantId) {
    if (!this.cartData || !this.cartData.items) return null;

    for (let i = 0; i < this.cartData.items.length; i++) {
      if (this.cartData.items[i].variant_id === variantId) {
        return {
          ...this.cartData.items[i],
          index: i + 1 // Cart API uses 1-based indexing for line items
        };
      }
    }     
    return null;
  }

  getSectionsToRender() {
    return [
      'cart-drawer',
      'cart-icon-bubble',
      'main-cart-items',
      'main-cart-footer'
    ];
  }

  appendSectionsToFormData(formData) {
    if (!formData || formData.has('sections')) return;
    formData.append('sections', this.getSectionsToRender());
    formData.append('sections_url', window.location.pathname);
  }

  applyOptimisticAddToCart(variantId, quantity) {
    const qtyToAdd = Number(quantity) || 0;
    if (qtyToAdd <= 0) return;

    if (!this.cartData || !Array.isArray(this.cartData.items)) {
      this.cartData = { items: [] };
    }

    const existingItem = this.cartData.items.find((item) => item.variant_id === Number(variantId));
    if (existingItem) {
      existingItem.quantity = (Number(existingItem.quantity) || 0) + qtyToAdd;
    } else {
      this.cartData.items.push({
        variant_id: Number(variantId),
        quantity: qtyToAdd,
      });
    }

    // Local optimistic cache is now stale against server totals; allow next sync fetch.
    this.lastCartFetchAt = 0;
  }
showCartSuccessBadge(message = 'Item added to cart. Please proceed to checkout.') {
  let badge = document.querySelector('.custom-cart-success-badge');

  if (!badge) {
    badge = document.createElement('div');
    badge.className = 'custom-cart-success-badge';
    document.body.appendChild(badge);
  }

  badge.innerHTML = `
    <span class="custom-cart-success-icon">✓</span>
    <span>${message}</span>
  `;

  badge.classList.add('show');

  clearTimeout(this.cartSuccessBadgeTimer);

  this.cartSuccessBadgeTimer = setTimeout(() => {
    badge.classList.remove('show');
  }, 4000);
}
  showMaxAvailableWarning(wrapper, maxAvailable, variantId) {
    // console.log(`[ATC] Showing max available warning for variant ${variantId}: ${maxAvailable}`);
    
    // Find or create warning message
    let warningMsg = wrapper.querySelector('.custom-max-qty-warning');
    if (!warningMsg) {
      warningMsg = document.createElement('div');    
      warningMsg.className = 'custom-max-qty-warning';
      wrapper.appendChild(warningMsg);
    }
    
    warningMsg.innerHTML = `<small style="color: #d32f2f; display: block; padding: 8px 0;">Max available: ${maxAvailable}</small>`;
    
    // Auto-hide after 3 seconds
    setTimeout(() => {
      warningMsg.innerHTML = ''; 
    }, 3000);
  }

  showLoader(element, show = true) {
    if (!element) return;
    
    let spinner = element.querySelector('.loading__spinner');
    
    if (show) {
      if (!spinner) {
        spinner = document.createElement('div');
        spinner.className = 'loading__spinner';
        spinner.innerHTML = `
          <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
            <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
          </svg>
        `;
        element.appendChild(spinner);
      }
      spinner.classList.remove('hidden');
    } else {
      if (spinner) {
        spinner.classList.add('hidden');  
      }
    }
  }

  refreshCartDrawer(openDrawer = false) {
    this.pendingDrawerOpen = this.pendingDrawerOpen || openDrawer;
    if (this.sectionRefreshScheduled) return;
    this.sectionRefreshScheduled = true;

    const sections = ['cart-icon-bubble', 'cart-drawer', 'main-cart-items', 'main-cart-footer'];
    
    requestAnimationFrame(() => {
      const shouldOpenDrawer = this.pendingDrawerOpen;
      this.pendingDrawerOpen = false;
      this.sectionRefreshScheduled = false;

      try {
        fetch(`/?sections=${sections.join(',')}`, { credentials: 'same-origin', cache: 'no-store' })
          .then(res => res.json())
          .then(data => {
            this.updateCartSectionsFromData(data, shouldOpenDrawer);
          })
          .catch(e => console.error('Error refreshing cart drawer:', e));
      } catch (e) {
        console.error('Error refreshing cart drawer:', e);
      }
    });
  }

  updateCartSectionsFromData(sections, openDrawer = false) {
    if (!sections) return;

    // Update cart icon bubble - ONLY update the count, preserve the rest
    if (sections['cart-icon-bubble']) {
      const icon = document.getElementById('cart-icon-bubble');
      if (icon) {
        // Parse the response to get just the count bubble
        const doc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
        const newCountBubble = doc.querySelector('.cart-count-bubble');
        
        if (newCountBubble) {
          // Find or create the count bubble in the existing icon
          let countBubble = icon.querySelector('.cart-count-bubble');
          if (!countBubble) {
            countBubble = document.createElement('div');
            countBubble.className = 'cart-count-bubble';
            icon.appendChild(countBubble);
          }
          // Update only the count bubble content
          countBubble.innerHTML = newCountBubble.innerHTML;
        }
      }
    } 

    // Update cart drawer
    if (sections['cart-drawer']) {
      const cartDrawerElement = document.querySelector('cart-drawer');
      const cartDrawerContent = document.querySelector('#CartDrawer');
      if (cartDrawerContent) {
        const doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
        const newDrawerContent = doc.querySelector('#CartDrawer');
        if (newDrawerContent) {
          cartDrawerContent.innerHTML = newDrawerContent.innerHTML;
          if (cartDrawerElement) {
            if (newDrawerContent.querySelector('.cart-drawer__warnings')) {
              cartDrawerElement.classList.add('is-empty');
            } else {
              cartDrawerElement.classList.remove('is-empty');
            }
            const overlay = cartDrawerElement.querySelector('#CartDrawer-Overlay');
            if (overlay) overlay.onclick = () => cartDrawerElement.close();

            // Only open if flag is true
            if (openDrawer) {
              setTimeout(() => cartDrawerElement.open(document.querySelector('.header')), 100);
            }
          }
        }
      }
    }

    // Update main cart items
    if (sections['main-cart-items']) {
      const mainCartItems = document.getElementById('main-cart-items');
      if (mainCartItems) {
        const doc = new DOMParser().parseFromString(sections['main-cart-items'], 'text/html');
        const newCartItems = doc.querySelector('#main-cart-items');
        if (newCartItems) {
          mainCartItems.innerHTML = newCartItems.innerHTML;
        }
      }
    }

    // Update cart footer
    if (sections['main-cart-footer']) {
      const mainCartFooter = document.getElementById('main-cart-footer');
      if (mainCartFooter) {
        const doc = new DOMParser().parseFromString(sections['main-cart-footer'], 'text/html');
        const newCartFooter = doc.querySelector('#main-cart-footer');
        if (newCartFooter) {
          mainCartFooter.innerHTML = newCartFooter.innerHTML;
        }
      }
    }

    // Dispatch events
    const eventDetails = { cart: this.cartData, source: 'custom-atc' };
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: eventDetails }));
    document.dispatchEvent(new CustomEvent('cart:build', { detail: eventDetails }));
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.customATCManager = new CustomATCManager();
  });
} else {   
  window.customATCManager = new CustomATCManager();
}

// Reinitialize when new product forms are dynamically added
if (window.MutationObserver) {
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.addedNodes.length) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === 1) { // Element node
            if (node.tagName === 'PRODUCT-FORM' || node.querySelector?.('product-form')) {
              // Re-run initialization for new product forms (excludes
              // ".cvp-row-form" — see initializeAllATCButtons()).
              const forms = node.querySelectorAll?.('product-form form:not(.cvp-row-form)') || [node.querySelector('form:not(.cvp-row-form)')];
              forms.forEach((form) => {
                if (window.customATCManager && form) {
                  window.customATCManager.initializeATCButton(form);
                }
              });
            }
          }
        });
      }
    });
  });

  observer.observe(document.body, {
    childList: true,
    subtree: true
  });
}


// -------------- Dynamic ATC Disable Based on Inventory -----------------
document.addEventListener('DOMContentLoaded', function () {
  const atcButton = document.querySelector('.custom_product_ATC');
  const productForm = document.querySelector('form[action="/cart/add"]');

  if (!atcButton || !productForm ) return;

  function getMaxInventory() {
    return parseInt(atcButton.dataset.maxQty || "1");
  }

  function getCurrentVariantId() {
    const input = productForm?.querySelector('[name="id"]');
    return input ? parseInt(input.value) : null;
  }

  function getQuantityToAdd() {
    const input = productForm?.querySelector('[name="quantity"]');
    return input ? parseInt(input.value) || 1 : 1;
  }

  async function fetchCart() {
    try {
      const response = await fetch('/cart.js');
      return await response.json();
    } catch (err) {
      console.error("Cart fetch error:", err);
      return null;
    }
  }

  async function updateButtonState() {
    const variantId = getCurrentVariantId();
    if (!variantId) return;

    const cart = await fetchCart();
    if (!cart) return;

    const item = cart.items.find(i => i.variant_id === variantId);
    const inCartQty = item ? item.quantity : 0;
    const maxInventory = getMaxInventory();

    if (inCartQty >= maxInventory) {
      atcButton.disabled = true;
      atcButton.classList.add('is-disabled');
      atcButton.textContent = 'Sold out';
      atcButton.cursorPointer = 'none';
    } else {
      atcButton.disabled = false;
      atcButton.classList.remove('is-disabled');
      atcButton.textContent = 'Add';
    }
  }

  updateButtonState();

  document.addEventListener('change', function (e) {
    if (e.target.name === 'id' || e.target.name === 'quantity') updateButtonState();
  });

  document.addEventListener('click', function () {
    updateButtonState();
  });
});

